import {
  createEffect,
  createSignal,
  For,
  type JSX,
  on,
  onCleanup,
} from "solid-js";
import { cn } from "@/libs/cn";

/**
 * La shell che morpha — stessa tecnica della SaveBar di questa pagina: un
 * contenitore `overflow-hidden` la cui larghezza/altezza è scritta in pixel da
 * un ResizeObserver sulla vista attiva, così il cambio di vista È il cambio di
 * forma. La vista uscente resta assoluta per la durata del crossfade.
 *
 * `data-ready` arriva dopo la prima misura: il mount non anima mai.
 * Promossa dal prototipo `linkedin-sync`; la shell viaggia a 200ms come
 * `.save-bar-box`, i crossfade delle viste a 150ms (D9).
 *
 * Dentro una `ExpandCard` la misura resta, ma il CSS spegne la transizione
 * finché la card VIAGGIA (`.xcard[data-anim]:not([data-arrived])`): lì la
 * taglia la comanda la card, e una shell che la insegue con 200ms di ritardo
 * farebbe arrivare il contenuto dopo la propria cornice. Ad arrivo avvenuto
 * comanda di nuovo la shell, e la card la segue.
 */

type Entry = { id: number; key: string };

export type MorphViewProps = {
  /** Discrimina la vista corrente; un cambio innesca morph + crossfade. */
  stateKey: string;
  render: (key: string) => JSX.Element;
  class?: string;
  /** Dove restano appuntate le viste uscenti (default: sinistra). */
  align?: "start" | "end";
  /** Larghezza fluida al 100%: la shell anima solo l'altezza. */
  fullWidth?: boolean;
};

/** 30ms sopra i 150ms del crossfade: la vista esce solo a fade concluso. */
const LEAVE_MS = 180;

/**
 * Focus per i bottoni primari delle viste: mai `autofocus` dentro la shell —
 * su un contenitore `overflow:hidden` ancora in transizione il focus scrolla
 * il contenuto per rivelare il bottone e il morph appare tagliato in alto.
 *
 * Solo desktop (D15/EC16): sotto `lg` o con puntatore grosso il focus
 * aprirebbe la tastiera sopra la card che sta ancora arrivando.
 */
export const autofocusSoft = (el: HTMLElement) => {
  if (window.matchMedia("(pointer: coarse)").matches) return;
  if (document.documentElement.clientWidth < 1024) return;
  setTimeout(() => el.focus({ preventScroll: true }), 260);
};

export const MorphView = (props: MorphViewProps) => {
  let shell: HTMLDivElement | undefined;
  let nextId = 1;

  const [entries, setEntries] = createSignal<Entry[]>([
    { id: 0, key: props.stateKey },
  ]);
  const [leaving, setLeaving] = createSignal<ReadonlySet<number>>(new Set());

  let leaveTimer: ReturnType<typeof setTimeout> | undefined;
  createEffect(
    on(
      () => props.stateKey,
      (key, prev) => {
        if (prev === undefined || key === prev) return;
        setLeaving(new Set(entries().map((entry) => entry.id)));
        setEntries((list) => [...list, { id: nextId++, key }]);
        clearTimeout(leaveTimer);
        leaveTimer = setTimeout(() => {
          const gone = leaving();
          setEntries((list) => list.filter((entry) => !gone.has(entry.id)));
          setLeaving(new Set<number>());
        }, LEAVE_MS);
      },
    ),
  );
  onCleanup(() => clearTimeout(leaveTimer));

  let observer: ResizeObserver | undefined;
  const observe = (el: HTMLElement) => {
    observer?.disconnect();
    observer = new ResizeObserver(() => {
      if (!shell) return;
      if (!props.fullWidth) shell.style.width = `${el.offsetWidth}px`;
      shell.style.height = `${el.offsetHeight}px`;
      if (!shell.hasAttribute("data-ready")) {
        requestAnimationFrame(() => shell?.setAttribute("data-ready", ""));
      }
    });
    observer.observe(el);
  };
  onCleanup(() => observer?.disconnect());

  // La shell non deve mai scrollare: qualunque scroll interno (es. un focus
  // durante la transizione) sposterebbe il contenuto sotto la maschera.
  const lockScroll = (el: HTMLDivElement) => {
    shell = el;
    el.addEventListener("scroll", () => {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    });
  };

  return (
    <div
      ref={lockScroll}
      class={cn("lsync-shell", props.class)}
      data-align={props.align ?? "start"}
      data-full={props.fullWidth ? "" : undefined}
    >
      <For each={entries()}>
        {(entry) => (
          <div
            class="lsync-view"
            data-leaving={leaving().has(entry.id) ? "" : undefined}
            ref={(el) => {
              if (!leaving().has(entry.id)) observe(el);
            }}
          >
            {props.render(entry.key)}
          </div>
        )}
      </For>
    </div>
  );
};
